import { useState, FC, JSX } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Container,
  Alert,
  Link,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { useNavigate } from 'react-router';
import { useSnackbar } from 'notistack';
import { getNotificationNavigationUrl } from 'src/utils/notificationNavigation';
import UnifiedHeader from 'src/components/UnifiedHeader';
import NotificationsTour, {
  TourButton,
} from 'src/components/Notifications/NotificationsTour';
import BulkNotificationDetails from 'src/components/Notifications/BulkNotificationDetails';
import MultiNotificationDetails from 'src/components/Notifications/MultiNotificationDetails';
import RefreshModal from 'src/components/Notifications/RefreshModal';
import {
  NotificationFilters,
  NotificationList,
} from 'src/components/Notifications';
import useAuth from 'src/hooks/useAuth';
import { useNotifications } from 'src/contexts/NotificationContext';
import { useNotificationFilters } from 'src/hooks/useNotificationFilters';
import { useEventTracking } from 'src/hooks/useEventTracking';
import {
  Notification,
  isMultiNotification,
  isBulkNotification,
} from 'src/types/Notifications';

const _formatActionType = (actionType: string): string => {
  const actionMap = {
    bulk_file_owner_assignment: 'File Owner Assignment',
    bulk_status_change: 'Status Change',
    bulk_property_transfer: 'Property Transfer',
    // Add more action types as needed
  };
  return (
    actionMap[actionType] ||
    actionType.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
  );
};

const NotificationsPage: FC = (): JSX.Element => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const { notifications, loading, error, fetchNotifications, bulkMarkAsRead } =
    useNotifications();
  const {
    trackError,
    trackNotificationClick,
    trackNotificationRefresh,
    trackNotificationHelpClick,
    trackNotificationTourClick,
    trackNotificationSettingsClick,
    trackNotificationAlertDismiss,
    trackNotificationBulkDetailsOpen,
    trackNotificationBulkDetailsClose,
  } = useEventTracking();

  const [refreshing, setRefreshing] = useState(false);
  const [hasNewNotifications, setHasNewNotifications] = useState(false);
  const [selectedBulkNotification, setSelectedBulkNotification] =
    useState<Notification | null>(null);
  const [selectedMultiNotification, setSelectedMultiNotification] =
    useState<Notification | null>(null);
  const [refreshModalOpen, setRefreshModalOpen] = useState(false);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const [showSettingsAlert, setShowSettingsAlert] = useState(() => {
    const ts = localStorage.getItem('notificationsSettingsAlertDismissed');
    if (!ts) {
      return true;
    }
    const dismissedAt = new Date(ts);
    const now = new Date();
    const daysSinceDismissed =
      (now.getTime() - dismissedAt.getTime()) / (1000 * 60 * 60 * 24);
    return daysSinceDismissed > 7; // Show again after 7 days
  });

  // Use the notification filters hook
  const {
    filterState,
    filterCounts,
    filteredNotifications,
    handleReadStatusChange,
    handleSourceChange,
    handleTypeChange,
    handleNotificationTypeChange,
    toggleFiltersExpanded,
  } = useNotificationFilters(notifications);

  const handleDismissSettingsAlert = (): void => {
    setShowSettingsAlert(false);
    localStorage.setItem(
      'notificationsSettingsAlertDismissed',
      new Date().toISOString()
    );
    trackNotificationAlertDismiss({
      current_filters: filterState,
      total_notifications: notifications.length,
      filtered_notifications: filteredNotifications.length,
      user_role: user?.role,
      user_permissions: {
        canUseRealTimeNotificationsFileOwner:
          user?.canUseRealTimeNotificationsFileOwner || false,
        canUseRealTimeNotificationsWatched:
          user?.canUseRealTimeNotificationsWatched || false,
        canUseRealTimeNotificationsProperty:
          user?.canUseRealTimeNotificationsProperty || false,
      },
      debug_info: {
        component_name: 'NotificationsPage',
        function_name: 'handleDismissSettingsAlert',
      },
    });
  };

  const handleRefresh = async (): Promise<void> => {
    setRefreshing(true);
    setHasNewNotifications(false);
    setRefreshModalOpen(true);
    setRefreshError(null);

    try {
      // Track refresh action
      trackNotificationRefresh({
        current_filters: filterState,
        total_notifications: notifications.length,
        filtered_notifications: filteredNotifications.length,
        user_role: user?.role,
        user_permissions: {
          canUseRealTimeNotificationsFileOwner:
            user?.canUseRealTimeNotificationsFileOwner || false,
          canUseRealTimeNotificationsWatched:
            user?.canUseRealTimeNotificationsWatched || false,
          canUseRealTimeNotificationsProperty:
            user?.canUseRealTimeNotificationsProperty || false,
        },
        debug_info: {
          component_name: 'NotificationsPage',
          function_name: 'handleRefresh',
        },
      });

      await fetchNotifications(true);
    } catch (error) {
      // Track refresh error
      trackError(error, {
        context: 'notification_page_refresh',
        current_filters: filterState,
      });
      setRefreshError('Failed to refresh notifications');
    } finally {
      setRefreshing(false);
    }
  };

  const handleNotificationClick = async (
    notif: Notification
  ): Promise<void> => {
    // Track notification click
    trackNotificationClick(notif.id, {
      notification_type: notif.notification_type,
      notification_source: notif.source,
      is_read: notif.is_read,
      current_filters: filterState,
      total_notifications: notifications.length,
      filtered_notifications: filteredNotifications.length,
      user_role: user?.role,
      user_permissions: {
        canUseRealTimeNotificationsFileOwner:
          user?.canUseRealTimeNotificationsFileOwner || false,
        canUseRealTimeNotificationsWatched:
          user?.canUseRealTimeNotificationsWatched || false,
        canUseRealTimeNotificationsProperty:
          user?.canUseRealTimeNotificationsProperty || false,
      },
      debug_info: {
        component_name: 'NotificationsPage',
        function_name: 'handleNotificationClick',
      },
    });

    // Handle bulk notification details
    if (isBulkNotification(notif)) {
      trackNotificationBulkDetailsOpen(notif.id, {
        current_filters: filterState,
        total_notifications: notifications.length,
        filtered_notifications: filteredNotifications.length,
        user_role: user?.role,
        user_permissions: {
          canUseRealTimeNotificationsFileOwner:
            user?.canUseRealTimeNotificationsFileOwner || false,
          canUseRealTimeNotificationsWatched:
            user?.canUseRealTimeNotificationsWatched || false,
          canUseRealTimeNotificationsProperty:
            user?.canUseRealTimeNotificationsProperty || false,
        },
        debug_info: {
          component_name: 'NotificationsPage',
          function_name: 'handleNotificationClick',
        },
      });
      setSelectedBulkNotification(notif);
    } else if (isMultiNotification(notif)) {
      // Handle multi-notification details
      trackNotificationBulkDetailsOpen(notif.id, {
        current_filters: filterState,
        total_notifications: notifications.length,
        filtered_notifications: filteredNotifications.length,
        user_role: user?.role,
        user_permissions: {
          canUseRealTimeNotificationsFileOwner:
            user?.canUseRealTimeNotificationsFileOwner || false,
          canUseRealTimeNotificationsWatched:
            user?.canUseRealTimeNotificationsWatched || false,
          canUseRealTimeNotificationsProperty:
            user?.canUseRealTimeNotificationsProperty || false,
        },
        debug_info: {
          component_name: 'NotificationsPage',
          function_name: 'handleNotificationClick',
        },
      });
      setSelectedMultiNotification(notif);
    } else {
      // Use centralized navigation utility for all other notification types
      const navigationUrl = getNotificationNavigationUrl(notif);
      if (navigationUrl) {
        if (notif.notification_type === 'new_documents') {
          window.open(navigationUrl, '_blank');
        } else {
          navigate(navigationUrl);
        }
      }
    }
  };

  // Help content for UnifiedHeader
  const helpContent = (
    <>
      <Typography
        variant="body2"
        component="div"
        sx={{ fontWeight: 700, mb: 2 }}
      >
        Notification Types & Consolidation
      </Typography>

      <Typography
        variant="body2"
        component="div"
        sx={{ fontWeight: 600, mb: 1 }}
      >
        Individual Notification Types:
      </Typography>
      <Typography variant="body2" component="div" sx={{ mb: 0.5 }}>
        <b>Email:</b> New inbound email received for a certification you own or
        watch
      </Typography>
      <Typography variant="body2" component="div" sx={{ mb: 0.5 }}>
        <b>SMS:</b> New inbound SMS message received for a certification you own
        or watch
      </Typography>
      <Typography variant="body2" component="div" sx={{ mb: 0.5 }}>
        <b>FAX:</b> New inbound fax received for a certification you own or
        watch
      </Typography>
      <Typography variant="body2" component="div" sx={{ mb: 0.5 }}>
        <b>Status Note:</b> New status note created for a certification you own
        or watch
      </Typography>
      <Typography variant="body2" component="div" sx={{ mb: 0.5 }}>
        <b>Document:</b> New document uploaded that requires compliance review
      </Typography>
      <Typography variant="body2" component="div" sx={{ mb: 2 }}>
        <b>File Owner:</b> You&apos;ve been assigned as file owner for a
        certification
      </Typography>

      <Typography
        variant="body2"
        component="div"
        sx={{ fontWeight: 600, mb: 1 }}
      >
        Consolidated Notifications:
      </Typography>
      <Typography variant="body2" component="div" sx={{ mb: 0.5 }}>
        <b>Automatic Consolidation:</b> Multiple notifications of the same type
        for the same certification are automatically grouped together within a
        30-minute window
      </Typography>
      <Typography variant="body2" component="div" sx={{ mb: 0.5 }}>
        <b>Multi-Event Icon:</b> Look for the stacked icon (⧉) indicating
        consolidated notifications with multiple events
      </Typography>
      <Typography variant="body2" component="div" sx={{ mb: 0.5 }}>
        <b>Event Count:</b> The number badge shows how many individual events
        are grouped together
      </Typography>
      <Typography variant="body2" component="div" sx={{ mb: 0.5 }}>
        <b>Attachment Tracking:</b> Individual attachment counts are preserved
        for each event within consolidated notifications
      </Typography>
      <Typography variant="body2" component="div" sx={{ mb: 2 }}>
        <b>Click to Expand:</b> Click on consolidated notifications to see all
        individual events with timestamps and details
      </Typography>

      <Typography
        variant="body2"
        component="div"
        sx={{ fontWeight: 600, mb: 1 }}
      >
        Notification Sources:
      </Typography>
      <Typography variant="body2" component="div" sx={{ mb: 0.5 }}>
        <b>File Owner:</b> Notifications for certifications you&apos;re assigned
        to manage
      </Typography>
      <Typography variant="body2" component="div" sx={{ mb: 0.5 }}>
        <b>Watched:</b> Notifications for certifications you&apos;re watching
        but don&apos;t own
      </Typography>
      <Typography variant="body2" component="div">
        <b>Property:</b> Property-level notifications for communication events
      </Typography>
    </>
  );

  // Permission check
  const hasNotificationPermission =
    user?.canUseRealTimeNotificationsFileOwner ||
    user?.canUseRealTimeNotificationsWatched ||
    user?.canUseRealTimeNotificationsProperty;

  if (!hasNotificationPermission) {
    return (
      <Container maxWidth="md" sx={{ mt: 8 }}>
        <Typography variant="h5" color="error" align="center">
          You do not have permission to view notifications.
        </Typography>
      </Container>
    );
  }

  return (
    <>
      <NotificationsTour>
        <UnifiedHeader
          heading="Notifications"
          showSettingsButton={true}
          showHelpButton={true}
          showRefreshButton={true}
          showTourButton={true}
          TourButtonComponent={TourButton}
          pageContext="notification"
          onSettingsClick={() => {
            trackNotificationSettingsClick({
              current_filters: filterState,
              total_notifications: notifications.length,
              filtered_notifications: filteredNotifications.length,
              user_role: user?.role,
              user_permissions: {
                canUseRealTimeNotificationsFileOwner:
                  user?.canUseRealTimeNotificationsFileOwner || false,
                canUseRealTimeNotificationsWatched:
                  user?.canUseRealTimeNotificationsWatched || false,
                canUseRealTimeNotificationsProperty:
                  user?.canUseRealTimeNotificationsProperty || false,
              },
              debug_info: {
                component_name: 'NotificationsPage',
                function_name: 'onSettingsClick',
              },
            });
            navigate('/workbench/user/settings');
          }}
          onHelpClick={() => {
            trackNotificationHelpClick({
              current_filters: filterState,
              total_notifications: notifications.length,
              filtered_notifications: filteredNotifications.length,
              user_role: user?.role,
              user_permissions: {
                canUseRealTimeNotificationsFileOwner:
                  user?.canUseRealTimeNotificationsFileOwner || false,
                canUseRealTimeNotificationsWatched:
                  user?.canUseRealTimeNotificationsWatched || false,
                canUseRealTimeNotificationsProperty:
                  user?.canUseRealTimeNotificationsProperty || false,
              },
              debug_info: {
                component_name: 'NotificationsPage',
                function_name: 'onHelpClick',
              },
            });
          }}
          onTourClick={() => {
            trackNotificationTourClick({
              current_filters: filterState,
              total_notifications: notifications.length,
              filtered_notifications: filteredNotifications.length,
              user_role: user?.role,
              user_permissions: {
                canUseRealTimeNotificationsFileOwner:
                  user?.canUseRealTimeNotificationsFileOwner || false,
                canUseRealTimeNotificationsWatched:
                  user?.canUseRealTimeNotificationsWatched || false,
                canUseRealTimeNotificationsProperty:
                  user?.canUseRealTimeNotificationsProperty || false,
              },
              debug_info: {
                component_name: 'NotificationsPage',
                function_name: 'onTourClick',
              },
            });
          }}
          onRefreshClick={handleRefresh}
          isRefreshing={refreshing}
          helpContent={helpContent}
        />
        <Container maxWidth="xl" sx={{ mb: 4, mt: 4 }}>
          {showSettingsAlert && (
            <Alert
              severity="info"
              sx={{ mb: 2 }}
              action={
                <IconButton
                  aria-label="close"
                  color="inherit"
                  size="small"
                  onClick={handleDismissSettingsAlert}
                >
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              }
            >
              You can manage your notification preferences in your{' '}
              <Link href="/workbench/user/settings" underline="hover">
                User Settings
              </Link>
              .
            </Alert>
          )}

          {/* New notifications alert */}
          {hasNewNotifications && (
            <Alert severity="info" sx={{ mb: 2 }}>
              New notifications are available. Click refresh to see them.
            </Alert>
          )}

          {/* Notification Filters */}
          <NotificationFilters
            notifications={notifications}
            filterState={filterState}
            filterCounts={filterCounts}
            onReadStatusChange={handleReadStatusChange}
            onSourceChange={handleSourceChange}
            onTypeChange={handleTypeChange}
            onNotificationTypeChange={handleNotificationTypeChange}
            onToggleFiltersExpanded={toggleFiltersExpanded}
          />

          {/* Loading State */}
          {loading ? (
            <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={32} />
            </Box>
          ) : error ? (
            <Typography color="error" sx={{ p: 2 }}>
              {error}
            </Typography>
          ) : (
            /* Notification List */
            <NotificationList
              notifications={filteredNotifications}
              onNotificationClick={handleNotificationClick}
            />
          )}
        </Container>
      </NotificationsTour>

      {/* Bulk Notification Details Dialog */}
      <BulkNotificationDetails
        notification={selectedBulkNotification}
        open={!!selectedBulkNotification}
        onClose={() => {
          if (selectedBulkNotification) {
            trackNotificationBulkDetailsClose(selectedBulkNotification.id, {
              current_filters: filterState,
              total_notifications: notifications.length,
              filtered_notifications: filteredNotifications.length,
              user_role: user?.role,
              user_permissions: {
                canUseRealTimeNotificationsFileOwner:
                  user?.canUseRealTimeNotificationsFileOwner || false,
                canUseRealTimeNotificationsWatched:
                  user?.canUseRealTimeNotificationsWatched || false,
                canUseRealTimeNotificationsProperty:
                  user?.canUseRealTimeNotificationsProperty || false,
              },
              debug_info: {
                component_name: 'NotificationsPage',
                function_name: 'onBulkDetailsClose',
              },
            });
          }
          setSelectedBulkNotification(null);
        }}
      />

      {/* Multi Notification Details Dialog */}
      <MultiNotificationDetails
        notification={selectedMultiNotification}
        open={!!selectedMultiNotification}
        onClose={() => setSelectedMultiNotification(null)}
      />

      {/* Refresh Modal */}
      <RefreshModal
        open={refreshModalOpen}
        onClose={() => setRefreshModalOpen(false)}
        error={refreshError}
        isRefreshing={refreshing}
      />
    </>
  );
};

export default NotificationsPage;
